import React from 'react';
import { Layout } from '../components/Layout';

export function About() {
  return (
    <Layout>
      {/* Header Section */}
      <section className="relative bg-white py-20 overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
            Company
          </p>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 tracking-tight mb-4">About AOK Financial</h1>
          <p className="text-xl text-gray-500 max-w-2xl">
            Building the future of US equity access for global brokerages
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
            Mission
          </p>
          <h2 className="text-3xl font-bold text-gray-900 mb-8 tracking-tight">Our Mission</h2>
          <div className="space-y-4 text-gray-600 leading-relaxed">
            <p>
              AOK Financial Inc. is an introducing broker-dealer dedicated to democratizing US equity market access for retail investors worldwide. We partner with brokerage platforms globally to provide seamless, zero-commission access to US stocks with features that legacy providers can't match.
            </p>
            <p>
              Our B2B approach means we focus on what we do best—trading infrastructure—while our partners focus on customer acquisition and user experience. This specialization allows us to deliver institutional-grade execution at retail-friendly pricing.
            </p>
            <p>
              What sets us apart is our commitment to modern technology and customer-centric features. By offering 24/5 trading and building our OMS in Rust for unparalleled performance, we're able to offer capabilities that align with global market hours, not just traditional US market hours.
            </p>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
            Team
          </p>
          <h2 className="text-3xl font-bold text-gray-900 mb-8 tracking-tight">Leadership</h2>
          <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
            <div className="flex items-start gap-6">
              <div className="bg-brand-50 border border-brand-200 w-20 h-20 rounded-full flex-shrink-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-brand-600">AK</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  Adam Kalinich
                </h3>
                <p className="text-brand-600 font-mono text-sm mb-4">
                  Founder & CEO
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Adam graduated from Massachusetts Institute of Technology (MIT) in 2016 with a degree in Mathematics and Computer Science. After graduation, he worked as a cryptocurrency researcher before transitioning to traditional finance, where he built order management systems for regulated brokerages.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Recognizing the opportunity to bring US market access to global investors with modern technology and better economics, Adam founded AOK Financial to bridge this gap with a focus on developer-friendly APIs, zero commissions, and extended trading hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advisors */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
            Advisory
          </p>
          <h2 className="text-3xl font-bold text-gray-900 mb-8 tracking-tight">Advisors</h2>
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="bg-brand-50 border border-brand-200 w-20 h-20 rounded-full flex-shrink-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-brand-600">PG</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    Pravil Gupta
                  </h3>
                  <p className="text-brand-600 font-mono text-sm mb-4">
                    Advisor
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Partner & CTO at Mathisys; former CTO of Quadeye Trading LLC; co-founder of Amuura Corp; former CTO at Selectica Inc. (publicly traded); 15+ years designing low-latency high-performance trading systems; Ph.D. in Computer Engineering and MSEE from University of Southern California; B.Tech in EE from IIT Mumbai; holds US patent on product configuration techniques.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="bg-brand-50 border border-brand-200 w-20 h-20 rounded-full flex-shrink-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-brand-600">SG</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    Sudeep Gupta
                  </h3>
                  <p className="text-brand-600 font-mono text-sm mb-4">
                    Advisor
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Founder of Quadeye Securities; former Managing Director, Global Strategic Risk Group at Merrill Lynch; founding member of Morgan Stanley's electronic trading group; MS/ABD Computer Sciences from Cornell University; B.Tech from IIT Kanpur; Adjunct Professor of Financial Engineering at NYU Tandon.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
            Partnerships
          </p>
          <h2 className="text-3xl font-bold text-gray-900 mb-8 tracking-tight">Partners</h2>
          <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              RQD Clearing & Custody
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              AOK Financial has established a clearing relationship with RQD, a registered broker-dealer and member of FINRA, SIPC, and NFA. Through this partnership, RQD provides:
            </p>
            <ul className="space-y-2 text-gray-600 text-sm ml-1">
              <li className="flex items-start">
                <span className="text-brand-600 mr-3 font-mono">--</span>
                Trade execution and clearing services
              </li>
              <li className="flex items-start">
                <span className="text-brand-600 mr-3 font-mono">--</span>
                Custody of customer securities
              </li>
              <li className="flex items-start">
                <span className="text-brand-600 mr-3 font-mono">--</span>
                Compliance and regulatory support
              </li>
              <li className="flex items-start">
                <span className="text-brand-600 mr-3 font-mono">--</span>
                Access to extended-hours and 24/5 trading
              </li>
            </ul>
            <p className="text-gray-600 text-sm leading-relaxed mt-4">
              This relationship allows AOK Financial to focus on technology and customer integration while relying on RQD's established infrastructure for regulatory compliance and market access.
            </p>
          </div>
        </div>
      </section>

      {/* Regulatory Status */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
            Compliance
          </p>
          <h2 className="text-3xl font-bold text-gray-900 mb-8 tracking-tight">
            Regulatory Status
          </h2>
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-start gap-4 shadow-sm">
              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  RQD Clearing Partnership
                </h3>
                <p className="text-xs text-emerald-600 font-mono uppercase tracking-wider mb-2">Established</p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Clearing and execution services provided through our established relationship with RQD, a FINRA/SIPC member firm.
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-start gap-4 shadow-sm">
              <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  SEC Registration
                </h3>
                <p className="text-xs text-amber-600 font-mono uppercase tracking-wider mb-2">In Progress</p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  AOK Financial is in the process of registering as a broker-dealer with the Securities and Exchange Commission.
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-start gap-4 shadow-sm">
              <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  FINRA Membership
                </h3>
                <p className="text-xs text-amber-600 font-mono uppercase tracking-wider mb-2">Application Pending</p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Application for Financial Industry Regulatory Authority membership is currently under review.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-gradient-to-r from-brand-600 to-brand-500 py-20 overflow-hidden">
        <div className="absolute inset-0 noise-texture" />
        <div className="container mx-auto px-4 max-w-4xl text-center relative z-10">
          <h2 className="text-4xl font-bold mb-4 text-white tracking-tight">Partner With Us</h2>
          <p className="text-xl mb-8 text-white/80">
            Learn how AOK Financial can power your US equity offering
          </p>
          <a
            href="/contact"
            className="bg-white text-brand-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-200 inline-block hover:scale-[1.02]"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </Layout>
  );
}
